import React from 'react';
import { find, get, isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// local dependencies
import { authFlowCallerIdentificationClient as AuthFlowCallerIdentificationClient } from 'ui/views/widgets/authFlowCallerIdentificationClient';
import { AuthFlowCallerIdentificationClientService } from 'services/authFlowCallerIdentificationClient';
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION, PAGE_LEVEL_PRELOADER_TEMPLATES } from 'constants/common';
import { REDUCER } from 'constants/entity-names';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { updateCallState, initCallState } from 'store/actions/callState';
import { isInbound, getCallerTypeFromName } from 'resolvers/callStateEntities';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AuthFlowCallerIdentificationClientWidget } from 'ui/containers/AuthFlowCallerIdentificationClientWidget';
 *
 * // render
 * <AuthFlowCallerIdentificationClientWidget propName='propValue'/>
 */
export class _AuthFlowCallerIdentificationClientWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods to instance
		this.__bind();

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
			mode: 'editable', // could be `readonly` or `editable`
            isSearchClientVisible: false, //search visibility
            clientInfo: {},
            isCriteriaVisible: false,
            isMemberVerified: false,
            actionsForNext: null,
            dataForMatchCriteria: null
        };

        // create service instance
        this.authFlowCallerIdentificationClientService = new AuthFlowCallerIdentificationClientService();
        
        // start data fetch
        this.fetchComponentData();
    }

    // bind methods and events to instance
	__bind() {

		this.handleToggleEditMode = this.handleToggleEditMode.bind( this );
        this.handleFormSubmit = this.handleFormSubmit.bind( this );
		this.onSelectClientSearch = this.onSelectClientSearch.bind( this );
		this.handleClientSearchQueryChange = this.handleClientSearchQueryChange.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );
        this.handleMarkVerifiedAndShowNext = this.handleMarkVerifiedAndShowNext.bind( this );
        this.saveCriteriaSelection = this.saveCriteriaSelection.bind( this );
	}

	/*********************************************/
	

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'AuthFlowCallerIdentificationClientWidget.fetchComponentData()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationClientService.cancel();

        // fetch component data
        const { status, data } = await this.authFlowCallerIdentificationClientService.fetchComponentData( {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
	}

	/* ************************************************************* */

	/**
     * @desc Action handler for next button click.
     */
    handleActionClick( { item = {} } = {} ) {
        log.debug( 'AuthFlowCallerIdentificationClientWidget.handleActionClick()'  );

        // get client details
        // const memberId = get( item, 'memberId', null );
        const firstName = get( item, 'firstName', '' );
        const lastName = get( item, 'lastName', '' );

        // extract actions
        const actions = get( this.state.dataForMatchCriteria, 'actions' );

        // getting caller type value
        const callerType = getCallerTypeFromName( this.props.widget.value.callerType );

        /*---------*/

        // if it's outbound call, move to the next step
        if( ! isInbound() ) {
            this.props.onNextActionClick( { name: 'nextOutbound' } );
        }

        /*---------*/

        // if its inbound call, register an inbound caller
        if( isInbound() ) {
            const action = find( actions, { name: 'nextInbound'} );

            return this.registerInboundCaller( {
                actionName: action.name,
                url: action.url,
                payload: {
                    id: this.state.clientId,
                    firstName,
                    lastName,
                },
                callerType: callerType
            } );
        }
    }

    /**
     * @desc Request backend to register `member` as the caller for the ongoing inbound call.
     */
    async registerInboundCaller( { actionName, url, payload, callerType } ) {
        log.debug( 'AuthFlowCallerIdentificationClientWidget.registerInboundCaller()' );

        // cancel ongoing request
        this.authFlowCallerIdentificationClientService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // fetch component data
        const { status } = await this.authFlowCallerIdentificationClientService.registerInboundCaller( {
            url: url,
            data: payload,
            callerType: callerType
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {

                // update call data in the store
                this.props.updateCallState( {
                    callerId: payload.clientId,
                    callerType: callerType,
                    callerInfo: {
                        id: null,
                        refId: null,
                        type: callerType,
                        firstName: null,
                        lastName: null,
                    },
                } );

                // move to the next step
                this.props.onNextActionClick( { name: actionName } );
            } );
        } else {
            errorOverlay.open(); // show error
        }

	}
	
    /************************************************************** */
    
    /**
     * @desc Mark member as verified (from criteria match widget).
     */
    handleMarkVerifiedAndShowNext( { isVerified = false } ) {
        log.debug( 'AuthFlowCallerIdentificationClientWidget.handleMarkVerifiedAndShowNext()' );

        this.setState( {
            isMemberVerified: isVerified,
        } );
    }

    /************************************************************** */

    /**
	 * @desc Toggle between read and edit mode.
	 */
	handleToggleEditMode( editable = true ) {
		log.debug( 'AuthFlowCallerIdentificationClientWidget.handleToggleEditMode() ' );

		this.setState( {
			mode: editable ? 'editable' : 'readonly',
		} );
    }
    
    /**
	 * @desc Handle form submit for caller/callee details
	 */
	async handleFormSubmit( { title, firstName, lastName, phone } = {} ) {
		log.debug( 'AuthFlowCallerIdentificationClientWidget.handleFormSubmit()' );

		// get save item action
		const saveAction = find( this.state.componentData.sections.callerDetails.actions, { name: 'save' } );

		// cancel ongoing request
		this.authFlowCallerIdentificationClientService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.authFlowCallerIdentificationClientService.addUpdateItem( {
			url: saveAction.url,
			data: {
                operation: isEmpty( this.state.clientInfo ) ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE,
				title,
                firstName,
                lastName,
                phone,
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState( {
				isFetching: false,
				isSearchClientVisible: true,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				mode: 'readonly',
				clientInfo: data,
				componentData: {
					...this.state.componentData,
				},
			} );
		} else {
			errorOverlay.open(); // show error
		}
    }

    /**
	 * @desc Handle form submit.
	 */
	async onSelectClientSearch( searchVal ) {
		log.debug( 'AuthFlowCallerIdentificationClientWidget.onSelectClientSearch()' );

		// cancel ongoing request
		this.authFlowCallerIdentificationClientService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const { status, data } = await this.authFlowCallerIdentificationClientService.fetchMatchCriteria( {
			url: this.state.componentData.sections.callerVerification.url,
			data: {
				id: searchVal
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				clientId: searchVal,
                isCriteriaVisible: true,
                dataForMatchCriteria: data,
                isMemberVerified: false,
			} );
		} else {
			errorOverlay.open(); // show error
		}
    }

    /**
     * @desc When client search query change, hide criteria view
     */
    handleClientSearchQueryChange() {
		log.debug( 'AuthFlowCallerIdentificationClientWidget.handleClientSearchQueryChange()' );

        if( this.state.isCriteriaVisible ) {
            this.setState( {
                isCriteriaVisible: false,
                isMemberVerified: false
            } );
        }
    }

    /**
     * @desc Update criteria selection on the backend.
     */
    async saveCriteriaSelection( { saveAction, fieldStatus } ) {
        log.debug( 'MatchCriteriaWidget.saveCriteriaSelection()' );

        if( isEmpty( saveAction ) ) {
            return;
        }

        /*----------*/

        // cancel ongoing request
        this.authFlowCallerIdentificationClientService.cancel();

        // show page level preloader
        pageLevelPreloader.show( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // get status and service data
        const { status } = await this.authFlowCallerIdentificationClientService.saveCriteriaSelection( {
            url: saveAction.url,
            data: {
                id: this.state.clientId,

                // ...pickBy( fieldStatus, ( { value } = {} ) => ! isNil( value ) ),
                ...fieldStatus
            },
        } );

        // hide page level preloader
        pageLevelPreloader.hide( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
            return null; // ignore on request cancellations
         } else if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
    */
    componentWillUnmount() {
        this.authFlowCallerIdentificationClientService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {

        return (
            <AuthFlowCallerIdentificationClient
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                hasCallStarted={ ! isEmpty( this.props.callState ) }
                componentData={ get( this.state, 'componentData', {} ) }
                onFormSubmit={ this.handleFormSubmit }
                onToggleEditMode={ this.handleToggleEditMode }
                mode={ this.state.mode }
                onSelectClientSearch={ this.onSelectClientSearch }
                onClientSearchQueryChange={ this.handleClientSearchQueryChange }
                isSearchClientVisible={ this.state.isSearchClientVisible }
                isCriteriaVisible={ this.state.isCriteriaVisible }
                clientId={ this.state.clientId }
                clientInfo={ this.state.clientInfo }
                handleActionClick={ this.handleActionClick }
                handleMarkVerifiedAndShowNext={ this.handleMarkVerifiedAndShowNext }
                isMemberVerified={ this.state.isMemberVerified }
                actionsForNext={ this.state.actionsForNext }
                dataForMatchCriteria={ this.state.dataForMatchCriteria }
                saveCriteriaSelection={ this.saveCriteriaSelection }
            />
        );
    }
}

// set display name
_AuthFlowCallerIdentificationClientWidget.displayName = 'AuthFlowCallerIdentificationClientWidget';

// set default props
_AuthFlowCallerIdentificationClientWidget.defaultProps = {};

// prop types
_AuthFlowCallerIdentificationClientWidget.propTypes = {};

/************************************************************ */

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

// return component with acccess to Redux store
export const AuthFlowCallerIdentificationClientWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _AuthFlowCallerIdentificationClientWidget ) );


